import { useRef, useState, useEffect, forwardRef } from "react";
import { Swiper } from "swiper/react";
import ChevronRightRounded from "../icons/chevron-right-rounded";
import ChevronLeftRounded from "../icons/chevron-left-rounded";
import { m } from "framer-motion";
import {
  Navigation,
  Pagination,
  Autoplay,
  EffectFade,
  FreeMode,
  Mousewheel,
} from "swiper/modules";

import cn from "classnames";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/autoplay";
import "swiper/css/free-mode";

const Carousel = ({
  children,
  className = "",
  total = 0,
  loop = true,
  slidesPerView,
  navigation = true,
  pagination = false,
  autoplay = false,
  activeIndex = 0,
  breakpoints,
  showNavAbove = true,
  showNavProject = false,
  customPagination = false,
  setCurrentSlide,
  title = "Read More Like this",
  initialSlide = 0,
  centeredSlides = false,
  description,
  inline = true,
  ...props
}) => {
  const [swiper, setSwiper] = useState();
  const [index, setIndex] = useState(initialSlide);
  const prevRef = useRef();
  const nextRef = useRef();

  useEffect(() => {
    if (swiper && swiper.navigation) {
      swiper.params.navigation.prevEl = prevRef.current;
      swiper.params.navigation.nextEl = nextRef.current;
      swiper.navigation.init();
      swiper.navigation.update();
      swiper.on("slideChange", function () {
        if (typeof setCurrentSlide === "function") {
          setIndex(swiper.realIndex);

          setCurrentSlide(swiper.realIndex);
        } else {
        }
      });
    }
  }, [swiper]);
  swiper?.on("slideChange", function (e) {
    setIndex(swiper.realIndex);
  });
  return (
    <div className={`carouselWrapper relative flex flex-col w-full`}>
      {showNavAbove && (
        <div className={`w-full ${inline ? "px-4" : "px-0"}`}>
          <div className="flex md:justify-between md:flex-row flex-col w-full md:pb-4 pb-4 px-4 border-b-[0.5px] border-darkGrey/50 mb-8 gap-4">
            <div className="flex flex-col gap-2 mb-2 md:mb-0">
              <p className="md:text-lg text-base font-havletica text-[#292929]/70 ">
                {title}
              </p>
              {description && (
                <p className="text-sm text-[#333333]/80 md:max-w-[400px] lg:max-w-[737px]">
                  {description}
                </p>
              )}
            </div>
            <div className="flex items-center md:justify-start justify-between md:w-fit w-full gap-4">
              <p className="font-light text-[#121111] text-sm">
                *Swipe or Drag to navigate
              </p>
              <div className="flex items-center gap-2 md:gap-3">
                <PrevButton
                  variant="top"
                  navigation={navigation}
                  ref={prevRef}
                />
                <NextButton
                  variant="top"
                  navigation={navigation}
                  ref={nextRef}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {showNavProject && (
        <div className="absolute bottom-10 right-4 flex items-center gap-3 z-[10]">
          <div className="flex items-center md:justify-start justify-between md:w-fit w-full gap-4">
            <p className="font-light text-[#121111] text-sm">
              *Swipe or Drag to navigate
            </p>
            <div className="flex items-center gap-2 md:gap-3">
              <PrevButton variant="top" navigation={navigation} ref={prevRef} />
              <NextButton variant="top" navigation={navigation} ref={nextRef} />
            </div>
          </div>
        </div>
      )}
      {!showNavAbove && navigation && (
        <PrevButton
          variant="side"
          navigation={navigation}
          ref={prevRef}
          className="hidden lg:block"
        />
      )}
      {!showNavAbove && navigation && (
        <NextButton
          variant="side"
          navigation={navigation}
          ref={nextRef}
          className="hidden lg:block"
        />
      )}
      <Swiper
        modules={[
          Navigation,
          Autoplay,
          Pagination,
          EffectFade,
          FreeMode,
          Mousewheel,
        ]}
        loop={loop}
        slidesPerView={slidesPerView}
        autoplay={autoplay}
        breakpoints={breakpoints}
        pagination={pagination}
        className="w-full"
        initialSlide={initialSlide}
        onSwiper={setSwiper}
        centeredSlides={centeredSlides}
        navigation={{
          prevEl: prevRef.current ? prevRef.current : undefined, // Assert non-null
          nextEl: nextRef.current ? nextRef.current : undefined, // Assert non-null
        }}
        {...props}
      >
        {children}
      </Swiper>
      {customPagination && (
        <div className="fixed bottom-0 w-full h-20 z-[10]">
          <div className="flex items-center justify-center gap-4 w-full h-full">
            {Array.from({ length: total }, (_, index) => index + 1).map(
              (row, key) => (
                <div
                  key={key}
                  className={`pagination-dot relative cursor-pointer ${
                    key === index ? "active" : ""
                  }`}
                  onClick={() => swiper?.slideTo(key)} // Manually navigate to the clicked slide
                >
                  <div className="loading" />
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};
const NextButton = forwardRef(({ scrolling, variant, className }, ref) => {
  return (
    <button
      ref={ref}
      aria-label="next-button"
      className={cn(
        `transition duration-300 ease-in-out ${scrolling ? "opacity-70" : ""}`,
        className,
        {
          "fixed top-1/2 md:right-8 right-2 -translate-y-1/2 transform z-[10] text-white":
            variant === "side",
          "relative text-darkGrey": variant === "top",
        }
      )}
    >
      <m.div
        // initial={{ opacity: 0, y: 100 }}
        // whileInView={{ opacity: 100, y: 0 }}
        // transition={{ duration: 1, delay: 0.5 }}
        // viewport={{ once: true }}
        className="md:w-8 md:h-8 w-6 h-6"
      >
        <ChevronRightRounded />
      </m.div>
    </button>
  );
});
NextButton.displayName = "NextButton";

const PrevButton = forwardRef(({ scrolling, variant, className }, ref) => {
  return (
    <button
      ref={ref}
      aria-label="prev-button"
      className={cn(
        `transition duration-300 ease-in-out ${scrolling ? "opacity-70" : ""}`,
        className,
        {
          "fixed top-1/2 md:left-8 left-2 -translate-y-1/2 transform text-white z-10":
            variant === "side",
          "relative text-darkGrey": variant === "top",
        }
      )}
    >
      <m.div
        className="md:w-8 md:h-8 w-6 h-6"
        // initial={{ opacity: 0, y: 100 }}
        // whileInView={{ opacity: 100, y: 0 }}
        // transition={{ duration: 1, delay: 0.5 }}
        // viewport={{ once: true }}
      >
        <ChevronLeftRounded />
      </m.div>
    </button>
  );
});
PrevButton.displayName = "PrevButton"; // Add displayName
export default Carousel;
